import React, { useState, useEffect } from 'react';
import { spacesAPI, bookingsAPI } from '../services/api';
import { FaPlus } from 'react-icons/fa';

const DashboardPage = ({ userType }) => {
  const [activeTab, setActiveTab] = useState(userType === 'owner' ? 'mySpaces' : 'myBookings');
  const [spaces, setSpaces] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSpace, setShowAddSpace] = useState(false);
  const [newSpace, setNewSpace] = useState({
    title: '',
    description: '',
    spaceType: 'shop',
    location: { address: '', city: '', state: '', zipCode: '' },
    area: '',
    pricePerMonth: '',
    amenities: '',
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'mySpaces' && userType === 'owner') {
        const response = await spacesAPI.getAll();
        const userId = localStorage.getItem('userId');
        setSpaces(response.data.filter(s => s.owner._id === userId));
      } else if (activeTab === 'myBookings') {
        const userId = localStorage.getItem('userId');
        const response = await bookingsAPI.getUserBookings(userId);
        setBookings(response.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSpace = async (e) => {
    e.preventDefault();
    try {
      const amenitiesArray = newSpace.amenities.split(',').map(a => a.trim());
      await spacesAPI.create({ ...newSpace, amenities: amenitiesArray });
      setShowAddSpace(false);
      setNewSpace({
        title: '',
        description: '',
        spaceType: 'shop',
        location: { address: '', city: '', state: '', zipCode: '' },
        area: '',
        pricePerMonth: '',
        amenities: '',
      });
      fetchData();
    } catch (error) {
      console.error('Error adding space:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-4xl font-bold mb-8 text-blue-800">Dashboard</h1>

        {/* Tabs */}
        <div className="flex space-x-4 mb-8 border-b">
          {userType === 'owner' && (
            <button
              onClick={() => setActiveTab('mySpaces')}
              style={activeTab === 'mySpaces' ? { borderBottomColor: '#2563eb', color: '#2563eb' } : {}}
              className={`pb-2 px-4 ${activeTab === 'mySpaces' ? 'border-b-2 font-semibold' : 'text-gray-600'}`}
            >
              My Spaces
            </button>
          )}
          <button
            onClick={() => setActiveTab('myBookings')}
            style={activeTab === 'myBookings' ? { borderBottomColor: '#2563eb', color: '#2563eb' } : {}}
            className={`pb-2 px-4 ${activeTab === 'myBookings' ? 'border-b-2 font-semibold' : 'text-gray-600'}`}
          >
            My Bookings
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-12">
            <div style={{ borderTopColor: '#2563eb', borderBottomColor: '#2563eb' }} className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2"></div>
          </div>
        ) : (
          <>
            {/* My Spaces Tab */}
            {activeTab === 'mySpaces' && userType === 'owner' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-semibold">My Listed Spaces</h2>
                  <button
                    onClick={() => setShowAddSpace(true)}
                    style={{ backgroundColor: '#2563eb' }}
                    className="text-white px-4 py-2 rounded-lg transition flex items-center hover:opacity-90"
                  >
                    <FaPlus className="mr-2" />
                    Add New Space
                  </button>
                </div>

                {showAddSpace && (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                    <h3 className="text-xl font-semibold mb-4">Add New Space</h3>
                    <form onSubmit={handleAddSpace} className="space-y-4">
                      <input
                        type="text"
                        placeholder="Title"
                        value={newSpace.title}
                        onChange={(e) => setNewSpace({ ...newSpace, title: e.target.value })}
                        required
                        className="w-full border rounded px-4 py-2"
                      />
                      <textarea
                        placeholder="Description"
                        value={newSpace.description}
                        onChange={(e) => setNewSpace({ ...newSpace, description: e.target.value })}
                        required
                        className="w-full border rounded px-4 py-2"
                        rows="3"
                      />
                      <select
                        value={newSpace.spaceType}
                        onChange={(e) => setNewSpace({ ...newSpace, spaceType: e.target.value })}
                        className="w-full border rounded px-4 py-2"
                      >
                        <option value="shop">Shop</option>
                        <option value="office">Office</option>
                        <option value="storage">Storage</option>
                        <option value="warehouse">Warehouse</option>
                        <option value="other">Other</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Address"
                        value={newSpace.location.address}
                        onChange={(e) => setNewSpace({ ...newSpace, location: { ...newSpace.location, address: e.target.value } })}
                        required
                        className="w-full border rounded px-4 py-2"
                      />
                      <input
                        type="text"
                        placeholder="City"
                        value={newSpace.location.city}
                        onChange={(e) => setNewSpace({ ...newSpace, location: { ...newSpace.location, city: e.target.value } })}
                        required
                        className="w-full border rounded px-4 py-2"
                      />
                      <input
                        type="number"
                        placeholder="Area (sq ft)"
                        value={newSpace.area}
                        onChange={(e) => setNewSpace({ ...newSpace, area: e.target.value })}
                        required
                        className="w-full border rounded px-4 py-2"
                      />
                      <input
                        type="number"
                        placeholder="Price per Month"
                        value={newSpace.pricePerMonth}
                        onChange={(e) => setNewSpace({ ...newSpace, pricePerMonth: e.target.value })}
                        required
                        className="w-full border rounded px-4 py-2"
                      />
                      <input
                        type="text"
                        placeholder="Amenities (comma separated)"
                        value={newSpace.amenities}
                        onChange={(e) => setNewSpace({ ...newSpace, amenities: e.target.value })}
                        className="w-full border rounded px-4 py-2"
                      />
                      <div className="flex space-x-4">
                        <button type="submit" style={{ backgroundColor: '#2563eb' }} className="text-white px-6 py-2 rounded hover:opacity-90">
                          Add Space
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowAddSpace(false)}
                          className="bg-gray-300 text-gray-700 px-6 py-2 rounded hover:bg-gray-400"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {spaces.length === 0 ? (
                  <p className="text-center text-gray-600 py-12">You haven't listed any spaces yet.</p>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {spaces.map((space) => (
                      <div key={space._id} className="bg-white rounded-lg shadow-md p-4">
                        <h3 className="text-lg font-semibold mb-2">{space.title}</h3>
                        <p className="text-gray-600 text-sm mb-2">{space.location.city}</p>
                        <p style={{ color: '#003d66' }} className="font-bold">₹{space.pricePerMonth}/month</p>
                        <p className={`text-sm mt-2 ${space.isAvailable ? 'text-green-600' : 'text-red-600'}`}>
                          {space.isAvailable ? 'Available' : 'Not Available'}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* My Bookings Tab */}
            {activeTab === 'myBookings' && (
              <div>
                <h2 className="text-2xl font-semibold mb-6">My Bookings</h2>
                {bookings.length === 0 ? (
                  <p className="text-center text-gray-600 py-12">You haven't made any bookings yet.</p>
                ) : (
                  <div className="space-y-4">
                    {bookings.map((booking) => (
                      <div key={booking._id} className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-xl font-semibold mb-2">{booking.space?.title}</h3>
                            <p className="text-gray-600">{booking.space?.location.city}</p>
                            <p className="text-sm text-gray-500 mt-2">
                              {new Date(booking.startDate).toLocaleDateString()} - {new Date(booking.endDate).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p style={{ color: '#003d66' }} className="text-2xl font-bold">₹{booking.totalPrice}</p>
                            <span className={`inline-block px-3 py-1 rounded-full text-sm mt-2 ${
                              booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                              booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {booking.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
